import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/layout/Navbar";

/**
 * Forgot-password page. Enumeration-safe by design: the backend returns the
 * same 200 whether or not the email is registered, so we just always show a
 * "check your inbox" state on success.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [throttled, setThrottled] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/forgot-password", { email });
      if (r.data?.throttled) {
        setThrottled(r.data.message || "Too many reset requests. Please try again later.");
        setSent(false);
        toast.error("Too many reset requests. Check your inbox or wait a while.");
      } else {
        setThrottled("");
        setSent(true);
        toast.success("Reset link sent. Check your inbox.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="container mx-auto px-4 pt-32 pb-16">
        <div className="max-w-md mx-auto">
          <div className="brut-card bg-white p-8">
            <div className="overline mb-2">Account recovery</div>
            <h1 className="font-display font-black text-3xl mb-6">Forgot password</h1>

            {sent ? (
              <div className="space-y-4" data-testid="forgot-success-state">
                <p className="text-sm font-mono leading-relaxed">
                  If an account exists for <b>{email}</b>, we&rsquo;ve emailed a reset link.
                  It&rsquo;s valid for 1&nbsp;hour.
                </p>
                <p className="text-xs font-mono text-muted-foreground">
                  Didn&rsquo;t get it? Check your spam folder, or&nbsp;
                  <button
                    onClick={() => setSent(false)}
                    className="underline font-bold"
                    data-testid="forgot-retry"
                  >try again</button>.
                </p>
                <Link to="/auth?mode=login" className="brut-btn w-full justify-center" data-testid="back-to-login">
                  Back to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <p className="text-sm font-mono leading-relaxed text-muted-foreground">
                  Enter the email tied to your LeadTitan account and we&rsquo;ll send you a secure link to choose a new password.
                </p>
                {throttled && (
                  <div
                    className="border-2 border-ink bg-acid p-4 text-sm font-mono leading-relaxed"
                    data-testid="forgot-throttled-message"
                  >
                    <div className="overline mb-1">// Slow down</div>
                    {throttled}
                  </div>
                )}
                <div>
                  <label className="overline block mb-2">Email</label>
                  <input
                    type="email"
                    className="brut-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-input-email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="brut-btn-primary w-full justify-center"
                  data-testid="forgot-submit"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </button>
                <div className="pt-2 text-center">
                  <Link to="/auth?mode=login" className="font-mono text-xs underline" data-testid="forgot-back-link">
                    ← Back to sign in
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
